import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';
import type { ComponentPageData, RawQuestionSettings } from '@/features/content/useComponent';
import { AttemptsField } from '@/features/questions/AttemptsField';
import {
  attemptsToSelectValue,
  resolveSectionAttempts,
  type ResolvedAttempts,
  type SectionAttempts,
} from '@/features/questions/attemptSettings';
import { ChapterTimingField } from '@/features/questions/ChapterTimingField';
import {
  describeHintSetting,
  describeSolutionsSetting,
  hintChoices,
  readHintSetting,
  readSolutionsSetting,
  resolveHintSetting,
  resolveSolutionsSetting,
  solutionsChoiceValue,
  SOLUTIONS_CHOICES,
} from '@/features/questions/questionTimingSettings';

import { useUpdateComponentSettings } from './hooks/useUpdateComponentSettings';
import styles from './QuestionSettingsDialog.module.css';

interface QuestionSettingsDialogProps {
  component: ComponentPageData;
  componentId: string;
  courseId: string;
  // The course's settings for this dialog's questions, what a chapter follows
  // when it sets nothing, so each "Use course default" can name the value.
  courseSettings?: RawQuestionSettings | null;
  mode: 'homework' | 'preLecture';
  onOpenChange(nextOpen: boolean): void;
  open: boolean;
}

interface SettingsFormState {
  attemptsExactAnswer: number;
  attemptsMultipleChoice: number;
  // '' follows the course: nothing is saved on the chapter. Otherwise the value
  // that is.
  hintsExactAnswer: string;
  hintsMultipleChoice: string;
  showSolutionsAfterInterval: string;
}

function getChapterSettings(
  component: ComponentPageData,
  mode: 'homework' | 'preLecture',
) {
  return mode === 'homework'
    ? component.controlPanel.questionSettings
    : component.controlPanel.preLectureQuestionSettings;
}

// The chapter's OWN settings, never the course's: a value the chapter follows
// from its course must show as "Use course default", not as a choice the
// instructor never made.
function getOwnSettings(
  component: ComponentPageData,
  mode: 'homework' | 'preLecture',
): RawQuestionSettings | null {
  const raw = component.rawComponent;
  const settings =
    mode === 'homework'
      ? raw.question_settings ?? raw.questionSettings
      : raw.pre_lecture_question_settings ?? raw.preLectureQuestionSettings;

  return (settings as RawQuestionSettings | null | undefined) ?? null;
}

function toSelectValue(stored: number | undefined) {
  return stored === undefined ? '' : String(stored);
}

// The attempts students get, and where each comes from. The page works them
// out with the course defaults; a page built without them (in a test) has
// only the chapter's own settings to go on.
function getAttempts(
  component: ComponentPageData,
  mode: 'homework' | 'preLecture',
): SectionAttempts {
  return (
    component.controlPanel.attempts?.[mode] ??
    resolveSectionAttempts(getChapterSettings(component, mode), null)
  );
}

function getInitialState(
  component: ComponentPageData,
  mode: 'homework' | 'preLecture',
): SettingsFormState {
  const settings = getOwnSettings(component, mode);
  const attempts = getAttempts(component, mode);
  const solutions = readSolutionsSetting(settings);

  return {
    attemptsExactAnswer: attemptsToSelectValue(attempts.exactAnswer.limit),
    attemptsMultipleChoice: attemptsToSelectValue(attempts.multipleChoice.limit),
    hintsExactAnswer: toSelectValue(readHintSetting(settings, 'exactAnswer')),
    hintsMultipleChoice: toSelectValue(readHintSetting(settings, 'multipleChoice')),
    showSolutionsAfterInterval: toSelectValue(
      solutions === undefined ? undefined : solutionsChoiceValue(solutions),
    ),
  };
}

// An attempts value is saved on the chapter only when the instructor changed
// it, or when the chapter already had its own. One the chapter follows from
// the course, or the default, is never copied onto it: a later change to the
// course default would then miss this chapter (issue #54).
function attemptsToSave(
  value: number,
  initialValue: number,
  initial: ResolvedAttempts,
): number | undefined {
  return value !== initialValue || initial.source === 'chapter' ? value : undefined;
}

// A hint or solutions value is saved on the chapter only when the instructor
// picks one, or the chapter already has one. "Use course default" deletes it,
// in both spellings, so a later change to the course reaches this chapter. It
// used to write 0 for all three on every save, over the course's values (issue
// #144).
function applyTimingChoice(
  settings: Record<string, unknown>,
  snakeKey: string,
  camelKey: string,
  value: string,
) {
  delete settings[camelKey];

  if (value === '') {
    delete settings[snakeKey];
    return;
  }

  settings[snakeKey] = Number(value);
}

// Only a stored number of attempts can be outside the list, so the choices come
// from the value the dialog opened with: it stays choosable after another.
function hintChoicesFor(initialValue: string) {
  return hintChoices(initialValue === '' ? undefined : Number(initialValue));
}

export function QuestionSettingsDialog({
  component,
  componentId,
  courseId,
  courseSettings,
  mode,
  onOpenChange,
  open,
}: QuestionSettingsDialogProps) {
  const mutation = useUpdateComponentSettings();
  const [formState, setFormState] = useState<SettingsFormState>(() =>
    getInitialState(component, mode),
  );
  const isPreLecture = mode === 'preLecture';
  // What the dialog opened with: the attempts are compared with it on save.
  const initialState = getInitialState(component, mode);
  const attempts = getAttempts(component, mode);
  // What a chapter that sets nothing follows, and where that comes from.
  const followedHintsExact = resolveHintSetting(null, courseSettings, 'exactAnswer');
  const followedHintsMultipleChoice = resolveHintSetting(
    null,
    courseSettings,
    'multipleChoice',
  );
  const followedSolutions = resolveSolutionsSetting(null, courseSettings);

  useEffect(() => {
    if (!open) {
      return;
    }

    setFormState(getInitialState(component, mode));
  }, [component, mode, open]);

  async function handleSave() {
    const exactAnswer = attemptsToSave(
      formState.attemptsExactAnswer,
      initialState.attemptsExactAnswer,
      attempts.exactAnswer,
    );
    const multipleChoice = attemptsToSave(
      formState.attemptsMultipleChoice,
      initialState.attemptsMultipleChoice,
      attempts.multipleChoice,
    );

    await mutation.mutateAsync({
      applyChanges: (draft) => {
        const currentSettings =
          mode === 'homework'
            ? draft.question_settings ?? draft.questionSettings ?? {}
            : draft.pre_lecture_question_settings ??
              draft.preLectureQuestionSettings ??
              {};
        const nextSettings: RawQuestionSettings = {
          ...(currentSettings as RawQuestionSettings),
          ...(exactAnswer !== undefined ? { num_attempts_exact_answer: exactAnswer } : {}),
          ...(multipleChoice !== undefined
            ? { num_attempts_multiple_choice: multipleChoice }
            : {}),
        };
        const timingSettings = nextSettings as Record<string, unknown>;

        applyTimingChoice(
          timingSettings,
          'num_attempts_to_show_hints_exact_answer',
          'numAttemptsToShowHintsExactAnswer',
          formState.hintsExactAnswer,
        );
        applyTimingChoice(
          timingSettings,
          'num_attempts_to_show_hints_multiple_choice',
          'numAttemptsToShowHintsMultipleChoice',
          formState.hintsMultipleChoice,
        );
        applyTimingChoice(
          timingSettings,
          'show_solutions_after_interval',
          'showSolutionsAfterInterval',
          formState.showSolutionsAfterInterval,
        );

        if (mode === 'homework') {
          draft.question_settings = nextSettings;
          draft.questionSettings = nextSettings;
        } else {
          draft.pre_lecture_question_settings = nextSettings;
          draft.preLectureQuestionSettings = nextSettings;
        }
      },
      component,
      componentId,
      courseId,
    });

    onOpenChange(false);
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>
              {isPreLecture ? 'Pre-Lecture Settings' : 'Section Question Settings'}
            </Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.subtitle}>
                Any settings you change here will take precedence over question
                settings set at the course level.
              </p>
            </Dialog.Description>
          </div>

          <div className={styles.body}>
            <AttemptsField
              field="multipleChoice"
              initialSource={attempts.multipleChoice.source}
              initialValue={initialState.attemptsMultipleChoice}
              label="Number of attempts for multiple choice questions"
              labelClassName={styles.selectField}
              level="chapter"
              onChange={(value) =>
                setFormState((current) => ({
                  ...current,
                  attemptsMultipleChoice: value,
                }))
              }
              value={formState.attemptsMultipleChoice}
            />

            <AttemptsField
              field="exactAnswer"
              initialSource={attempts.exactAnswer.source}
              initialValue={initialState.attemptsExactAnswer}
              label="Number of attempts for exact/numerical answer questions"
              labelClassName={styles.selectField}
              level="chapter"
              onChange={(value) =>
                setFormState((current) => ({
                  ...current,
                  attemptsExactAnswer: value,
                }))
              }
              value={formState.attemptsExactAnswer}
            />

            <ChapterTimingField
              followedLabel={describeHintSetting(followedHintsMultipleChoice.value)}
              followedSource={followedHintsMultipleChoice.source}
              label="Hints for multiple choice questions"
              labelClassName={styles.selectField}
              onChange={(value) =>
                setFormState((current) => ({
                  ...current,
                  hintsMultipleChoice: value,
                }))
              }
              options={hintChoicesFor(initialState.hintsMultipleChoice)}
              value={formState.hintsMultipleChoice}
            />

            <ChapterTimingField
              followedLabel={describeHintSetting(followedHintsExact.value)}
              followedSource={followedHintsExact.source}
              label="Hints for exact/numerical questions"
              labelClassName={styles.selectField}
              onChange={(value) =>
                setFormState((current) => ({
                  ...current,
                  hintsExactAnswer: value,
                }))
              }
              options={hintChoicesFor(initialState.hintsExactAnswer)}
              value={formState.hintsExactAnswer}
            />

            <ChapterTimingField
              followedLabel={describeSolutionsSetting(followedSolutions.value)}
              followedSource={followedSolutions.source}
              label="When students see solutions"
              labelClassName={styles.selectField}
              onChange={(value) =>
                setFormState((current) => ({
                  ...current,
                  showSolutionsAfterInterval: value,
                }))
              }
              options={SOLUTIONS_CHOICES}
              value={formState.showSolutionsAfterInterval}
            />
          </div>

          {mutation.isError ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {mutation.error.message}
            </p>
          ) : null}

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button className={styles.cancelButton} variant="secondary">
                CANCEL
              </Button>
            </Dialog.Close>
            <Button
              className={styles.saveButton}
              disabled={mutation.isPending}
              onClick={() => {
                void handleSave();
              }}
              variant="primary"
            >
              {mutation.isPending ? 'Saving…' : 'SAVE'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
